import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { 
  LayoutDashboard, 
  Monitor, 
  Smartphone, 
  Calendar, 
  Brain, 
  BarChart3, 
  Settings, 
  X, 
  LogOut,
  CreditCard,
  TestTube,
  Users,
  Activity,
  Megaphone,
  Tv
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@utils/cn'
import AIAgentPanel from '@components/AIAgent/AIAgentPanel'
import WatchedUserIndicator from '@components/UI/WatchedUserIndicator'
import UserService from '../../utils/services/UserService';
import { isDemoUser, isTestUser } from '@/utils/adminUtils'


const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Screens', href: '/screens', icon: Monitor },
  { name: 'Channels', href: '/user-channels', icon: Tv },
  { name: 'Devices', href: '/devices', icon: Smartphone },
  { name: 'Schedule', href: '/schedule', icon: Calendar },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'Subscriptions', href: '/subscriptions', icon: CreditCard },
  { name: 'Settings', href: '/settings', icon: Settings },
]

const testNavigation = [
  { name: 'AI Studio', href: '/ai-studio', icon: Brain },
  { name: 'Channel Management', href: '/channels', icon: Tv },
  { name: 'Channel Statistics', href: '/channel-statistics', icon: BarChart3 },
  { name: 'Ads Management', href: '/ads', icon: Megaphone },
  { name: 'User Management', href: '/test-user-service', icon: Users },
]

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const { currentUser, currentUserData, logout } = useAuth()

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo.png" 
                alt="MyDigiSign Logo" 
                className="h-8 w-auto"
              />
              <span className="text-xl font-bold text-gray-900">MyDigiSign</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigation
              .filter(item => {
                // Hide Subscriptions for demo users
                if (item.name === 'Subscriptions' && isDemoUser(currentUserData)) return false;
                // Hide Subscriptions and Analytics for test users
                if ((item.name === 'Subscriptions' || item.name === 'Analytics') && isTestUser(currentUserData)) return false;
                return true;
              })
              .map((item) => {
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors",
                    isActive
                      ? "bg-primary-50 text-primary-700 border-r-2 border-primary-500"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className={cn(
                    "mr-3 h-5 w-5",
                    isActive ? "text-primary-500" : "text-gray-400"
                  )} />
                  <span className="flex-1">{item.name}</span>
                  {item.name === 'Channels' && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                      FREE
                    </span>
                  )}
                </Link>
              )
            })}
            
            {/* Management Navigation (Admin Only) */}
            {UserService.isUserAdmin(currentUserData) && (
              <>
                <div className="pt-6 pb-2">
                  <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Management
                  </h3>
                </div>
                {testNavigation.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors",
                        isActive
                          ? "bg-yellow-50 text-yellow-700 border-r-2 border-yellow-500"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      )}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className={cn(
                        "mr-3 h-5 w-5",
                        isActive ? "text-yellow-500" : "text-gray-400"
                      )} />
                      {item.name}
                    </Link>
                  )
                })}
              </>
            )}
          </nav>

          {/* User section */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">
                  {currentUserData?.displayName?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {currentUserData?.displayName || 'User'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {currentUser?.email}
                </p>
              </div>
              <button
                onClick={logout}
                className="p-1 text-gray-400 hover:text-gray-600"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 lg:ml-64 lg:mr-80">
        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>

      {/* AI Agent Panel */}
      <AIAgentPanel />
      
      {/* Watched User Indicator */}
      <WatchedUserIndicator />
    </div>
  )
}

export default Layout 